import { useCallback, useEffect, useRef, useState } from 'react'

import type { IconName } from '@molecule/app-icons'
import { useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Flex, Icon, Tooltip } from '@molecule/app-ui-react'

import { Header } from '../components/Header.js'
import { MoleculeSpinner } from '../components/MoleculeSpinner.js'
import { STATUS_API_BASE_URL } from '../config.js'
import {
  type ComponentStatus,
  consolidateIncidents,
  dayTone,
  deriveComponentRows,
  formatCheckedAt,
  formatDayLabel,
  formatIncidentDuration,
  formatIncidentRange,
  formatMonitoredSpan,
  formatRoughDuration,
  formatUptimePercent,
  headlineStatus,
  type HealthProbe,
  type HealthResponse,
  historyIsStale,
  normalizeStatusHistory,
  OUTAGE_CONFIRM_FAILURES,
  STATUS_COMPONENT_KEYS,
  STATUS_LABEL,
  type StatusComponentKey,
  type StatusHistory,
  type StatusTone,
  statusTone,
  summarizeIncidents,
  unreachableHeadline,
} from '../lib/status.js'
import { fetchWatchdogFeed, type WatchdogFeed } from '../lib/watchdog.js'

/**
 * Public, unauthenticated status page for Molecule.dev. Polls the Molecule.dev
 * API from OUTSIDE its infrastructure ({@link STATUS_API_BASE_URL}, default
 * `https://api.molecule.dev`) and renders one row per subsystem from its coarse
 * `/health` `components` map — API, Database, Sandboxes & Builds, Realtime &
 * WebSockets, and Redis & Queue. This decomposition is the fix for a single
 * conflated "API" row, under which a Redis or build-queue outage read as "all
 * systems operational".
 *
 * Honest and live: no fabricated uptime, no green-by-default. All status
 * derivation lives in `lib/status.ts`; all styling goes through `getClassMap()`
 * theme tokens (no hardcoded hex). On-brand light/dark.
 */

/** The root-level `/health` URL on the monitored Molecule.dev API. */
function getHealthUrl(): string {
  return `${STATUS_API_BASE_URL.replace(/\/$/, '')}/health`
}

/** The `/api/status/history` endpoint on the monitored Molecule.dev API. */
function getHistoryUrl(): string {
  return `${STATUS_API_BASE_URL.replace(/\/$/, '')}/api/status/history`
}

/** The `/api/status/rss` subscribe endpoint on the monitored Molecule.dev API. */
function getRssUrl(): string {
  return `${STATUS_API_BASE_URL.replace(/\/$/, '')}/api/status/rss`
}

const POLL_INTERVAL_MS = 30_000

/** The molecule icon-set glyph for each status tone. */
const TONE_ICON: Record<StatusTone, IconName> = {
  success: 'check-circle',
  warning: 'exclamation-triangle',
  error: 'x-circle',
  neutral: 'info-circle',
}

/** The ClassMap text-color token for a status tone (sets `currentColor`). */
function toneColorVar(tone: StatusTone): string {
  if (tone === 'success') return 'var(--mol-color-success)'
  if (tone === 'warning') return 'var(--mol-color-warning)'
  if (tone === 'neutral') return 'var(--mol-color-text-tertiary)'
  return 'var(--mol-color-error)'
}

/**
 * Static, label/description metadata for each subsystem row. The label +
 * description make every row self-explanatory (discoverability), and the keys
 * mirror the API's `HEALTH_COMPONENT_KEYS`. Redis & Queue are intentionally one
 * row: the build queue runs on Redis, so they share a fate.
 */
const COMPONENT_META: Record<StatusComponentKey, { name: string; desc: string }> = {
  api: { name: 'API', desc: 'Core HTTP API & app endpoints' },
  db: { name: 'Database', desc: 'Control-plane Postgres' },
  tenantDb: {
    name: 'Tenant Databases',
    desc: 'Tenant Postgres — every project’s database, and the one deploys and deployed apps open (degraded when it does not answer, which fails new projects and deploys)',
  },
  sandboxes: { name: 'Sandboxes & Builds', desc: 'Dev containers, builds & deploys' },
  realtime: {
    name: 'Realtime & WebSockets',
    desc: 'Live collaboration, presence & locks (degraded while Redis is down or timing out)',
  },
  redis: {
    name: 'Redis & Queue',
    desc: 'Cache, rate limiting & the build queue: reachable and answering commands in time',
  },
  aiDefaults: {
    name: 'AI Defaults',
    desc: 'Platform-chosen models resolve in the running API (no live request is sent)',
  },
  migrations: {
    name: 'Schema Migrations',
    desc: 'Database migrations applied on the last API boot',
  },
  fleet: {
    name: 'Sandbox Fleet',
    desc: 'Sandbox servers accept new sandboxes (degraded if one refuses, or none has room)',
  },
  alerting: {
    name: 'Ops Alerting',
    desc: 'Platform alerts have a destination (degraded when an incident would reach no one)',
  },
  deployments: {
    name: 'Deployments',
    desc: 'User deploys reach ready (degraded when every recent deploy across several projects failed)',
  },
  sandboxCreation: {
    name: 'Sandbox Creation',
    desc: 'New sandboxes start (degraded when a notable share of recent creations failed)',
  },
  backups: {
    name: 'Project Backups',
    desc: 'Project backups are written encrypted to object storage, and to an immutable store when one is configured (degraded when a store or its protection is missing, when a restore waits for an operator, or when a running project\u2019s database could not be included in its backups)',
  },
}

/** A tone-colored SVG status glyph (never an emoji / generic dot). */
function StatusIcon({ status, size = 18 }: { status: ComponentStatus; size?: number }) {
  const cm = getClassMap()
  const tone = statusTone(status)
  return (
    <span
      aria-hidden
      className={cm.shrink0}
      style={{ display: 'inline-flex', color: toneColorVar(tone) }}
    >
      <Icon name={TONE_ICON[tone]} size={size} />
    </span>
  )
}

export function Status() {
  const cm = getClassMap()
  const { t } = useTranslation()
  const [probe, setProbe] = useState<HealthProbe | null>(null)
  const [checkedAt, setCheckedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [history, setHistory] = useState<StatusHistory | null>(null)
  // The history request failed: say so, and do not keep showing the last figure as if it were current.
  const [historyFailed, setHistoryFailed] = useState(false)
  // The site's OWN monitor feed (netlify/functions) — readable while the API
  // is dark, which is exactly when the API-served history above is not.
  const [watchdog, setWatchdog] = useState<WatchdogFeed | null>(null)
  // The health endpoint answered 429: keep the last good result instead of painting an outage.
  const [rateLimited, setRateLimited] = useState(false)
  const hadProbe = useRef(false)
  // Consecutive failed probes, and when the first of them happened: one dropped request is not an
  // outage, so the page keeps the last good result until a second one fails.
  const failures = useRef(0)
  const [downSince, setDownSince] = useState<number | null>(null)
  const healthUrl = useRef(getHealthUrl())
  const historyUrl = useRef(getHistoryUrl())

  const check = useCallback(async () => {
    try {
      const res = await fetch(healthUrl.current, { signal: AbortSignal.timeout(5_000) })
      // Too many requests from this network is not an outage: keep what was last seen.
      if (res.status === 429 && hadProbe.current) {
        setRateLimited(true)
        setLoading(false)
        return
      }
      setRateLimited(false)
      failures.current = 0
      setDownSince(null)
      let body: HealthResponse | null = null
      try {
        body = (await res.json()) as HealthResponse
      } catch (_error) {
        // Best-effort JSON parse: the fetch succeeded but the body is not valid
        // JSON. body=null is handled below and does not cause a crash.
        body = null
      }
      setProbe({ reached: true, ok: res.ok, body })
      hadProbe.current = true
      setCheckedAt(body?.timestamp ?? new Date().toISOString())
    } catch (error) {
      console.error('Health check fetch failed', error)
      failures.current += 1
      if (failures.current === 1) setDownSince(Date.now())
      if (hadProbe.current && failures.current < OUTAGE_CONFIRM_FAILURES) {
        setLoading(false)
        return
      }
      setProbe({ reached: false })
      setCheckedAt(new Date().toISOString())
    }
    setLoading(false)
  }, [])

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(historyUrl.current, { signal: AbortSignal.timeout(5_000) })
      if (!res.ok) {
        setHistory(null)
        setHistoryFailed(true)
        return
      }
      const body = (await res.json()) as { data?: StatusHistory }
      if (body?.data) {
        setHistory(normalizeStatusHistory(body.data))
        setHistoryFailed(false)
      }
    } catch (error) {
      // History is supplementary to the live banner — a failure here must not break the page; the
      // live status above is the primary signal. But the figure from the last success is dropped:
      // a stale uptime next to a fresh banner reads as current.
      console.error('Status history fetch failed', error)
      setHistory(null)
      setHistoryFailed(true)
    }
  }, [])

  const loadWatchdog = useCallback(async () => {
    setWatchdog(await fetchWatchdogFeed())
  }, [])

  useEffect(() => {
    check()
    loadHistory()
    loadWatchdog()
    const interval = setInterval(() => {
      check()
      loadHistory()
      loadWatchdog()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [check, loadHistory, loadWatchdog])

  // One row per subsystem — derived from the coarse public component map.
  // Before the first probe resolves the spinner is shown, so the placeholder
  // rows below are never actually rendered.
  const components = probe
    ? deriveComponentRows(probe)
    : STATUS_COMPONENT_KEYS.map((key) => ({ key, status: 'operational' as ComponentStatus }))
  // An unreachable API reads amber until it has stayed gone for a while (the independent monitor's
  // open incident counts too, so a page opened mid-outage knows how long it has lasted).
  const unreachable = probe != null && !probe.reached
  const outageStartedAt = [
    downSince,
    ...(watchdog?.incidents ?? [])
      .filter((i) => i.closedAt == null)
      .map((i) => Date.parse(i.openedAt)),
  ].filter((ms): ms is number => ms != null && Number.isFinite(ms))
  const downForMs = outageStartedAt.length > 0 ? Date.now() - Math.min(...outageStartedAt) : 0
  const overall = unreachable
    ? unreachableHeadline(downForMs)
    : headlineStatus(components, probe?.reached ? probe.body?.status : undefined)
  const overallTone = statusTone(overall)

  // Finished interruptions shorter than 15 minutes share one quiet line instead of a red row each.
  const incidentGroups = history ? consolidateIncidents(history.incidents) : []
  const incidentSummary = summarizeIncidents(incidentGroups)

  const statusLabelKey = unreachable ? 'unreachable' : overall
  const statusLabelDefault = unreachable ? 'Unreachable' : STATUS_LABEL[overall]

  return (
    <>
      <Header />
      {/* Inline maxWidth, NOT cm.maxW('[720px]'): that composes the arbitrary
          class at runtime where Tailwind's scanner can't see it, so it was
          never generated and the page rendered full-width. 1280 matches the
          header's inner max width. */}
      <div
        className={cm.cn(cm.sp('pt', 16), cm.sp('pb', 16), cm.sp('px', 4), cm.mxAuto)}
        style={{ maxWidth: 1080 }}
        data-mol-id="status-page"
      >
        <h1 className={cm.cn(cm.textSize('2xl'), cm.fontWeight('bold'), cm.sp('mb', 1))}>
          {t('status.title', undefined, { defaultValue: 'System Status' })}
        </h1>
        <p className={cm.cn(cm.textMuted, cm.textSize('sm'), cm.sp('mb', 6))}>
          {t('status.subtitle', undefined, {
            defaultValue:
              'Live status of Molecule.dev services. This page reflects the current health check only.',
          })}
        </p>

        {loading && !probe ? (
          <Flex align="center" justify="center" className={cm.sp('py', 12)}>
            <MoleculeSpinner />
          </Flex>
        ) : (
          <>
            {/* Overall banner */}
            <div
              className={cm.cn('mol-card', cm.sp('p', 5), cm.sp('mb', 5))}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
              }}
              data-mol-id="status-overall"
            >
              <StatusIcon status={overall} size={24} />
              <div className={cm.flex1}>
                <div className={cm.cn(cm.textSize('lg'), cm.fontWeight('bold'))}>
                  {overall === 'operational'
                    ? t('status.allOperational', undefined, {
                        defaultValue: 'All systems operational',
                      })
                    : unreachable && overall === 'degraded'
                      ? t('status.apiBlip', undefined, {
                          defaultValue: 'API temporarily unreachable',
                        })
                      : overall === 'degraded'
                        ? t('status.someDegraded', undefined, {
                            defaultValue: 'Some systems degraded',
                          })
                        : t('status.outage', undefined, { defaultValue: 'Service disruption' })}
                </div>
                <div className={cm.cn(cm.textSubtle, cm.textSize('xs'), cm.sp('mt', 0.5))}>
                  {t('status.lastChecked', undefined, { defaultValue: 'Last checked' })}:{' '}
                  {formatCheckedAt(checkedAt)}
                  {rateLimited &&
                    ` · ${t('status.rateLimited', undefined, {
                      defaultValue: 'Too many requests from your network; showing the last result',
                    })}`}
                </div>
                {probe != null && !probe.reached && (
                  <div
                    data-mol-id="status-api-unreachable"
                    className={cm.cn(cm.textSize('xs'), cm.sp('mt', 1))}
                    style={{ color: toneColorVar(overall === 'down' ? 'error' : 'warning') }}
                  >
                    {t('status.apiUnreachable', undefined, {
                      defaultValue:
                        'API unreachable — the health check timed out or failed. Retrying every 30 seconds.',
                    })}
                  </div>
                )}
              </div>
              <span
                className={cm.cn(
                  cm.textSize('xs'),
                  cm.fontWeight('bold'),
                  cm.uppercase,
                  cm.trackingWide,
                )}
                style={{ color: toneColorVar(overallTone) }}
                data-mol-id="status-overall-label"
              >
                {t(`status.label.${statusLabelKey}`, undefined, {
                  defaultValue: statusLabelDefault,
                })}
              </span>
            </div>

            {/* Per-component rows — one per subsystem */}
            <div
              data-mol-id="status-components"
              className={cm.card()}
              style={{ overflow: 'hidden' }}
            >
              {components.map((c, i) => {
                const meta = COMPONENT_META[c.key]
                return (
                  <div
                    key={c.key}
                    className={cm.cn(
                      cm.flex({ align: 'center', justify: 'between', gap: 'md' }),
                      cm.sp('px', 5),
                      cm.sp('py', 3),
                      i === 0 ? '' : cm.borderT,
                    )}
                    data-mol-id={`status-component-${c.key}`}
                  >
                    <div className={cm.flex({ align: 'center', gap: 'sm' })}>
                      <StatusIcon status={c.status} />
                      <div>
                        <div className={cm.cn(cm.textSize('sm'), cm.fontWeight('semibold'))}>
                          {t(`status.component.${c.key}.name`, undefined, {
                            defaultValue: meta.name,
                          })}
                        </div>
                        <div className={cm.cn(cm.textSubtle, cm.textSize('xs'))}>
                          {t(`status.component.${c.key}.desc`, undefined, {
                            defaultValue: meta.desc,
                          })}
                        </div>
                      </div>
                    </div>
                    <span
                      className={cm.cn(cm.textSize('sm'), cm.fontWeight('semibold'))}
                      style={{ color: toneColorVar(statusTone(c.status)) }}
                      data-mol-id={`status-component-${c.key}-label`}
                    >
                      {t(`status.label.${c.status}`, undefined, {
                        defaultValue: STATUS_LABEL[c.status],
                      })}
                    </span>
                  </div>
                )
              })}
            </div>

            {historyFailed && (
              <div
                data-mol-id="status-history-unavailable"
                className={cm.cn(
                  'mol-card',
                  cm.sp('p', 4),
                  cm.sp('mt', 6),
                  cm.textMuted,
                  cm.textSize('sm'),
                )}
              >
                {t('status.history.unavailable', undefined, {
                  defaultValue: 'Incident history is unavailable right now.',
                })}
              </div>
            )}

            {/* The independent monitor feed
                (netlify/functions/incidents.mjs — a passthrough of a monitor
                that runs on its OWN infrastructure, not on this site and not on
                the platform the API runs on). It was mol-watchdog, a Fly Machine
                in `fra`; since 2026-10-16 it prefers the Cloudflare Worker
                (molecule-dev/deploy/cloudflare/watchdog) and falls back to the
                Fly one, so a Fly-wide incident cannot take the record of that
                incident down with it. The API-served history above dies with the
                API; this feed does not — so during an outage THIS is the honest
                record: when it started, whether it is ongoing, and what the
                auto-start did. (The comment here used to name
                netlify/functions/uptime.mjs and Netlify Blobs — that variant was
                parked at uptime.mjs.disabled and never ran.) */}
            {watchdog &&
              watchdog.incidents.length > 0 &&
              (historyFailed || (probe != null && !probe.reached)) && (
                <div
                  data-mol-id="status-watchdog"
                  className={cm.cn('mol-card', cm.sp('p', 4), cm.sp('mt', 6))}
                >
                  <Flex align="center" justify="between" className={cm.sp('mb', 2)}>
                    <h2 className={cm.cn(cm.textSize('lg'), cm.fontWeight('bold'))}>
                      {t('status.watchdog.title', undefined, {
                        defaultValue: 'Independent monitor feed',
                      })}
                    </h2>
                    <span className={cm.cn(cm.textSubtle, cm.textSize('xs'))}>
                      {t(
                        'status.watchdog.watching',
                        { app: watchdog.monitored.app ?? 'molecule-api' },
                        {
                          defaultValue: `probing ${watchdog.monitored.app ?? 'molecule-api'} every minute, from independent infrastructure`,
                        },
                      )}
                    </span>
                  </Flex>
                  {[...watchdog.incidents]
                    .reverse()
                    .slice(0, 10)
                    .map((incident) => {
                      const ongoing = incident.closedAt == null
                      const durationMs = incident.closedAt
                        ? Math.max(0, Date.parse(incident.closedAt) - Date.parse(incident.openedAt))
                        : Math.max(0, Date.now() - Date.parse(incident.openedAt))
                      const healText = incident.heals?.length
                        ? incident.heals
                            .map((h) =>
                              h.error
                                ? t(
                                    'status.watchdog.healFailed',
                                    { error: h.error },
                                    {
                                      defaultValue: `auto-start FAILED (${h.error})`,
                                    },
                                  )
                                : t('status.watchdog.healed', undefined, {
                                    defaultValue: 'auto-start attempted',
                                  }),
                            )
                            .join('; ')
                        : (incident.note ??
                          t('status.watchdog.noHeal', undefined, {
                            defaultValue: 'no heal was attempted',
                          }))
                      return (
                        <div
                          key={incident.openedAt}
                          data-mol-id={`status-watchdog-incident-${incident.openedAt}`}
                          className={cm.cn(cm.sp('mb', 2), cm.textSize('sm'))}
                        >
                          <Flex align="center" className={cm.sp('mb', 1)}>
                            <span
                              className={cm.cn(cm.fontWeight('semibold'), cm.sp('mr', 2))}
                              style={{ color: toneColorVar(ongoing ? 'error' : 'neutral') }}
                            >
                              {ongoing
                                ? t('status.watchdog.ongoing', undefined, {
                                    defaultValue: 'Ongoing',
                                  })
                                : formatIncidentDuration(durationMs)}
                            </span>
                            <span className={cm.textMuted}>
                              {formatIncidentRange(incident.openedAt, incident.closedAt)}
                            </span>
                          </Flex>
                          <div className={cm.textMuted}>
                            {incident.lastError} · {healText}
                          </div>
                        </div>
                      )
                    })}
                </div>
              )}

            {/* Incident history — last 30 days, derived from recorded health
                samples. Honest: never shown until the API returns it. */}
            {history && (
              <div className={cm.sp('mt', 6)} data-mol-id="status-history">
                <Flex align="center" justify="between" className={cm.sp('mb', 2)}>
                  <h2 className={cm.cn(cm.textSize('lg'), cm.fontWeight('bold'))}>
                    {t('status.history.title', undefined, { defaultValue: 'Incident history' })}
                  </h2>
                  <span className={cm.cn(cm.textSubtle, cm.textSize('xs'))}>
                    {history.uptimePercent != null
                      ? t(
                          'status.history.uptime',
                          {
                            percent: formatUptimePercent(history.uptimePercent),
                            span: formatMonitoredSpan(history),
                          },
                          {
                            defaultValue: `${formatUptimePercent(history.uptimePercent)} uptime over the last ${formatMonitoredSpan(history)}`,
                          },
                        )
                      : t('status.history.noData', undefined, {
                          defaultValue: 'No history recorded yet',
                        })}
                  </span>
                </Flex>

                {history.lastSampleAt && (
                  <div
                    data-mol-id="status-history-fresh"
                    className={cm.cn(cm.textSize('xs'), cm.sp('mb', 2))}
                    style={
                      historyIsStale(history.lastSampleAt)
                        ? { color: toneColorVar('warning') }
                        : { opacity: 0.7 }
                    }
                  >
                    {t('status.history.lastRecorded', undefined, {
                      defaultValue: 'History last recorded',
                    })}
                    : {formatCheckedAt(history.lastSampleAt)}
                    {historyIsStale(history.lastSampleAt) &&
                      ` · ${t('status.history.stale', undefined, {
                        defaultValue:
                          'recording has stopped, so the figures above may be out of date',
                      })}`}
                  </div>
                )}

                {(history.unobservedMs ?? 0) > 0 && (
                  <div
                    data-mol-id="status-history-recording-gap"
                    className={cm.cn(cm.textSize('xs'), cm.sp('mb', 2))}
                    style={{ color: toneColorVar('warning') }}
                  >
                    {t(
                      'status.history.recordingGap',
                      {
                        duration: formatRoughDuration(history.unobservedMs ?? 0),
                      },
                      {
                        defaultValue: `Monitoring data is missing for ${formatRoughDuration(history.unobservedMs ?? 0)}. This time is excluded from uptime.`,
                      },
                    )}
                  </div>
                )}

                {/* Per-day status bar — equal-width columns so the bar fills the
                    width regardless of day count; each cell carries a themed
                    Tooltip (not a native title). */}
                <div
                  data-mol-id="status-history-bar"
                  className={cm.sp('mb', 2)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${history.days.length || 1}, minmax(0, 1fr))`,
                    gap: 3,
                  }}
                >
                  {history.days.map((d) => {
                    const tone = dayTone(d)
                    const dayStatusLabel =
                      d.status === 'no-data'
                        ? t('status.history.dayNoData', undefined, { defaultValue: 'No data' })
                        : tone === 'success' && d.status !== 'operational'
                          ? t('status.history.dayBrief', undefined, {
                              defaultValue: 'Brief interruption',
                            })
                          : (d.unobservedMs ?? 0) > 0 && tone !== 'error'
                            ? t('status.history.dayIncomplete', undefined, {
                                defaultValue: 'Incomplete monitoring data',
                              })
                            : t(`status.label.${d.status}`, undefined, {
                                defaultValue: STATUS_LABEL[d.status],
                              })
                    return (
                      <Tooltip
                        key={d.date}
                        placement="top"
                        content={`${formatDayLabel(d.date)} — ${dayStatusLabel}${
                          d.downMs ? ` · down ${formatIncidentDuration(d.downMs)}` : ''
                        }${
                          d.degradedMs ? ` · degraded ${formatIncidentDuration(d.degradedMs)}` : ''
                        }${
                          d.unobservedMs
                            ? ` · ${t('status.history.dayMissing', { duration: formatIncidentDuration(d.unobservedMs) }, { defaultValue: `no records ${formatIncidentDuration(d.unobservedMs)}` })}`
                            : ''
                        }`}
                      >
                        <span
                          data-mol-id={`status-history-day-${d.date}`}
                          aria-label={`${formatDayLabel(d.date)} — ${dayStatusLabel}`}
                          className={tone === 'muted' ? cm.bgBorder : ''}
                          style={{
                            display: 'block',
                            height: 28,
                            borderRadius: 2,
                            ...(tone === 'muted'
                              ? { opacity: 0.4 }
                              : { background: toneColorVar(tone) }),
                          }}
                        />
                      </Tooltip>
                    )
                  })}
                </div>
                <Flex align="center" justify="between" className={cm.sp('mb', 4)}>
                  <span className={cm.cn(cm.textSubtle, cm.textSize('xs'))}>
                    {t(
                      'status.history.windowStart',
                      { days: history.windowDays },
                      { defaultValue: `${history.windowDays} days ago` },
                    )}
                  </span>
                  <span className={cm.cn(cm.textSubtle, cm.textSize('xs'))}>
                    {t('status.history.windowEnd', undefined, { defaultValue: 'Today' })}
                  </span>
                </Flex>

                {/* One line for visitors: how long the service was affected in total, roughly, and
                    why. Per-window times, ranges and component chips were noise for them
                    (owner, 2026-10-19). */}
                {history.incidents.length === 0 ? (
                  <div
                    data-mol-id="status-history-empty"
                    className={cm.cn('mol-card', cm.sp('p', 4), cm.textMuted, cm.textSize('sm'))}
                  >
                    {t(
                      'status.history.empty',
                      { span: formatMonitoredSpan(history) },
                      {
                        defaultValue: `No incidents in the last ${formatMonitoredSpan(history)}.`,
                      },
                    )}
                  </div>
                ) : (
                  <div
                    data-mol-id="status-history-summary"
                    className={cm.cn('mol-card', cm.sp('px', 5), cm.sp('py', 4))}
                  >
                    <div className={cm.cn(cm.textSize('sm'), cm.fontWeight('semibold'))}>
                      {t(
                        'status.history.summary',
                        {
                          duration: formatRoughDuration(incidentSummary.totalMs),
                          span: formatMonitoredSpan(history),
                        },
                        {
                          defaultValue: `Affected ${formatRoughDuration(incidentSummary.totalMs)} in the last ${formatMonitoredSpan(history)}.`,
                        },
                      )}
                      {incidentSummary.ongoing && (
                        <span className={cm.cn(cm.textSize('xs'), cm.sp('ml', 2))}>
                          {t('status.history.ongoing', undefined, { defaultValue: 'Ongoing' })}
                        </span>
                      )}
                    </div>
                    <div
                      data-mol-id="status-history-cause"
                      className={cm.cn(cm.textMuted, cm.textSize('xs'), cm.sp('mt', 1))}
                    >
                      {incidentSummary.causes.length > 0
                        ? incidentSummary.causes.join(' ')
                        : t('status.history.causeUnrecorded', undefined, {
                            defaultValue: 'Short interruptions; the cause was not recorded.',
                          })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <Flex align="center" justify="between" className={cm.sp('mt', 4)}>
              <Flex align="center" gap="sm">
                <span className={cm.cn(cm.textSubtle, cm.textSize('xs'))}>
                  {t('status.refreshNote', undefined, {
                    defaultValue: 'Auto-refreshes every 30 seconds.',
                  })}
                </span>
                <a
                  href={getRssUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-mol-id="status-rss"
                  className={cm.cn(cm.link, cm.textSubtle, cm.textSize('xs'))}
                >
                  {t('status.subscribeRss', undefined, { defaultValue: 'Subscribe (RSS)' })}
                </a>
              </Flex>
              <button
                type="button"
                className="mol-btn"
                onClick={() => {
                  setLoading(true)
                  check()
                  loadHistory()
                }}
                data-mol-id="status-refresh"
              >
                {t('status.refresh', undefined, { defaultValue: 'Refresh' })}
              </button>
            </Flex>
          </>
        )}
      </div>
    </>
  )
}
